import { useCallback } from "react";
import { useToast } from "@racoon/ui";
import { useT, Namespace } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import {
  useCreateRouteWithJobsMutation,
  useUpdateRouteWithJobsMutation,
} from "~/shared/api/routes/route.grpc";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { useJobPickerModal } from "~/shared/ui/job-picker-modal";
import { isSingleJobPerTripRoute } from "./msw-route-list/jobCategoryRouteCompatibility";

export interface OpenAddJobsToRouteOptions {
  /** The trip being added to — its shift, depots and category seed any new trip. */
  route: ConnectProto.Route;
  /** Jobs already on the route — hidden from the picker and kept on save. */
  currentJobIds: bigint[];
  /** Day the picker opens on (defaults to today inside the picker). */
  initialDate?: Date;
  onAdded?: () => void;
}

/**
 * "Add jobs to route" — the shared job picker (~/shared/ui/job-picker-modal)
 * plus the persistence the routes page needs: the picked jobs are appended to
 * the route's existing ones through UpdateRouteWithJobs, and the result is
 * reported as a toast. The picker closes itself on confirm.
 *
 * A trip served by a single-job truck (construction / multilift) is the
 * exception: it holds one job and no more, so extra jobs each open a new trip on
 * the same shift instead of joining this one — the same split "Добавить маршрут"
 * performs while the route is still being built.
 */
export function useAddJobsToRouteModal() {
  const { openJobPicker } = useJobPickerModal();
  const { toast } = useToast();
  const t = useT();
  const { activeCompanyId } = useAuthSession();
  const [updateRouteWithJobs] = useUpdateRouteWithJobsMutation();
  const [createRouteWithJobs] = useCreateRouteWithJobsMutation();

  const openAddJobsToRoute = useCallback(
    ({ route, currentJobIds, initialDate, onAdded }: OpenAddJobsToRouteOptions) => {
      const ownerOrganizationId = BigInt(activeCompanyId ?? 1);
      const routeId = BigInt(route.id);
      const singleJobPerTrip = isSingleJobPerTripRoute(route);

      openJobPicker({
        serviceProviderIds: activeCompanyId ? [BigInt(activeCompanyId)] : [],
        truckId: route.driverOnShift?.truck?.id,
        executorId: route.ownerOrganizationId,
        initialDate,
        excludeJobIds: currentJobIds.map(Number),
        onConfirm: async (jobIds, pickedJobs) => {
          // The picker narrows ids to `number` for its own selection bookkeeping,
          // which cannot hold a job id past 2^53. The jobs it hands back still
          // carry the original bigint, so they are what the ids are read from.
          const originalIdOf = new Map(pickedJobs.map((job) => [Number(job.id), job.id]));
          const pickedJobIds = jobIds.map((jobId) => originalIdOf.get(jobId) ?? BigInt(jobId));

          const alreadyOnRoute = new Set(currentJobIds.map(String));
          const addedJobIds = pickedJobIds.filter((jobId) => !alreadyOnRoute.has(String(jobId)));
          if (addedJobIds.length === 0) return;

          // Everything the trip keeps: on a single-job trip only the first
          // picked job can stay here, and only while the trip is still empty.
          const keptOnRoute = singleJobPerTrip
            ? currentJobIds.length > 0
              ? currentJobIds
              : [addedJobIds[0]]
            : [...currentJobIds, ...addedJobIds];
          const jobsForNewTrips = addedJobIds.filter(
            (jobId) => !keptOnRoute.some((kept) => String(kept) === String(jobId)),
          );

          // Cloning the trip needs its shift and its unloading point; a route
          // missing either cannot seed one, so the operator is told rather than
          // silently handed an overfull trip.
          const canCreateTrips =
            route.driverOnShiftId != null &&
            route.destinationLocationId != null &&
            route.destinationLat != null &&
            route.destinationLon != null;

          if (jobsForNewTrips.length > 0 && !canCreateTrips) {
            toast({
              title: t(Namespace.Routes, "error"),
              description: t(Namespace.Routes, "jobsAddError"),
              variant: "destructive",
            });
            return;
          }

          // Each trip is its own RPC — the backend offers no batch — so a failure
          // part way through leaves the earlier ones created. Counting them is
          // what lets the operator be told exactly what landed.
          let createdTrips = 0;

          try {
            // UpdateRouteWithJobs replaces the whole job list, so the jobs already
            // on the route have to be sent along with the newly picked ones.
            const allJobIds = [...new Set(keptOnRoute.map(String))].map(BigInt);

            // A full single-job trip keeps exactly the jobs it had, so there is
            // nothing to update — every picked job opens a trip of its own.
            if (allJobIds.length !== currentJobIds.length) {
              await updateRouteWithJobs({
                route: { id: routeId },
                jobIds: allJobIds,
                ownerOrganizationId,
              }).unwrap();
            }

            // One backend route per trip — the same shape "Добавить маршрут"
            // submits, so the driver app sees one CO job per trip either way.
            for (const jobId of jobsForNewTrips) {
              await createRouteWithJobs({
                create: {
                  ownerOrganizationId,
                  driverOnShiftId: route.driverOnShiftId,
                  sourceLat: route.sourceLat ?? route.destinationLat,
                  sourceLon: route.sourceLon ?? route.destinationLon,
                  destinationLat: route.destinationLat,
                  destinationLon: route.destinationLon,
                  destinationLocationId: route.destinationLocationId,
                  ...(route.sourceLocationId != null && {
                    sourceLocationId: route.sourceLocationId,
                  }),
                  routeCategory: route.routeCategory,
                },
                ownerOrganizationId,
                jobIds: [jobId],
              }).unwrap();
              createdTrips++;
            }

            toast({
              title: t(Namespace.Routes, "success"),
              description:
                jobsForNewTrips.length > 0
                  ? t(Namespace.Routes, "jobsAddedAsNewTrips")
                  : t(Namespace.Routes, "jobsAddedSuccess"),
            });
            onAdded?.();
          } catch (error) {
            console.error("Failed to add jobs to route:", error);
            // The jobs that did not make it stay unrouted, so repeating the
            // action for them is safe — but only if the operator knows which
            // half already landed.
            const remaining = jobsForNewTrips.length - createdTrips;
            toast({
              title: t(Namespace.Routes, "error"),
              description:
                createdTrips > 0
                  ? t(Namespace.Routes, "jobsAddPartialError", { created: createdTrips, remaining })
                  : t(Namespace.Routes, "jobsAddError"),
              variant: "destructive",
            });
            // Trips that were created still have to appear in the list.
            if (createdTrips > 0) onAdded?.();
          }
        },
      });
    },
    [activeCompanyId, openJobPicker, updateRouteWithJobs, createRouteWithJobs, toast, t],
  );

  return { openAddJobsToRoute };
}
